import { useCallback, useEffect, useRef, useState } from "react";

export interface UseUndoStackOptions<T> {
	/** Live current value. Pushed onto the stack on debounced change. */
	current: T;
	/** Equality check; default is JSON.stringify. */
	equals?: (a: T, b: T) => boolean;
	/** When false, no entries are recorded (editor closed, etc.). */
	enabled: boolean;
	/** Max history entries. Oldest dropped FIFO. Default 5. */
	capacity?: number;
	/** Debounce window (ms) — combines rapid changes into one entry. Default 400. */
	debounceMs?: number;
}

export interface UseUndoStackReturn<T> {
	canUndo: boolean;
	/** Pop the most recent snapshot, returning it (or null if empty). */
	undo: () => T | null;
	/** Clear all history. Call on editor open/close. */
	reset: () => void;
	/**
	 * Suppress the NEXT debounced record. Use when applying an undo or a
	 * programmatic reset so the restore doesn't itself land on the stack.
	 */
	suppressNext: () => void;
}

const defaultEquals = <T,>(a: T, b: T): boolean =>
	JSON.stringify(a) === JSON.stringify(b);

/**
 * Lightweight undo stack with debounced capture. Tracks one value of any
 * shape; rapid edits (e.g. typing) collapse into a single history entry.
 *
 * Design: a useEffect watches `current`. When it diverges from the last
 * captured snapshot, a setTimeout schedules a push; subsequent changes
 * within `debounceMs` reset the timer so a burst of edits yields one
 * stack entry, not many.
 *
 * The consumer is responsible for restoring component state from the
 * returned snapshot when `undo()` is called.
 */
export const useUndoStack = <T,>({
	current,
	enabled,
	equals = defaultEquals,
	capacity = 5,
	debounceMs = 400,
}: UseUndoStackOptions<T>): UseUndoStackReturn<T> => {
	const [stackSize, setStackSize] = useState(0);
	const pastRef = useRef<T[]>([]);
	const lastCapturedRef = useRef<T>(current);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const suppressRef = useRef(false);

	const clearTimer = useCallback(() => {
		if (timerRef.current !== null) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (!enabled) return;
		if (equals(current, lastCapturedRef.current)) return;

		if (suppressRef.current) {
			suppressRef.current = false;
			lastCapturedRef.current = current;
			return;
		}

		clearTimer();
		const snapshotToPush = lastCapturedRef.current;
		timerRef.current = setTimeout(() => {
			pastRef.current = [...pastRef.current, snapshotToPush].slice(-capacity);
			lastCapturedRef.current = current;
			setStackSize(pastRef.current.length);
			timerRef.current = null;
		}, debounceMs);

		return clearTimer;
	}, [current, enabled, equals, capacity, debounceMs, clearTimer]);

	const undo = useCallback((): T | null => {
		clearTimer();
		if (pastRef.current.length === 0) return null;
		const next = [...pastRef.current];
		const snapshot = next.pop()!;
		pastRef.current = next;
		lastCapturedRef.current = snapshot;
		suppressRef.current = true;
		setStackSize(next.length);
		return snapshot;
	}, [clearTimer]);

	const reset = useCallback(() => {
		clearTimer();
		pastRef.current = [];
		lastCapturedRef.current = current;
		suppressRef.current = false;
		setStackSize(0);
	}, [clearTimer, current]);

	const suppressNext = useCallback(() => {
		suppressRef.current = true;
	}, []);

	return {
		canUndo: stackSize > 0,
		undo,
		reset,
		suppressNext,
	};
};
